import { useState, useEffect } from "react";
import { Alert, Button, Form, Modal, ListGroup, Spinner, Table, Collapse, Badge } from "react-bootstrap";
import { deleteNewProduct, updateNewProduct, getNewProductPlaces } from "../../apiForNewProducts";
import { getProductWorks, bulkUpdateProductWorks, getProductBudgetValues, updateProductBudgetValues } from "../../apiForNewProducts";
import { getBudgets } from "../../../../Analytic/Pages/Budgets/budgets";

export default function EditNewProduct({
    show,
    onHide,
    jwt,
    onUpdate,
    onDeleted,
    categories = [],
    product = {},
    places = []
}) {
    const [productLocal, setProductLocal] = useState({
        id: product?.id || 0,
        name: product?.name || '',
        coefficient: product?.coefficient || 1,
        categoryId: product?.categoryId || null,
        placeIds: [],
        productType: product?.productType ?? 0
    });

    const [onDelete, setOnDelete] = useState(null);
    const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [budgets, setBudgets] = useState([]);
    const [budgetValues, setBudgetValues] = useState({});
    const [budgetsLoading, setBudgetsLoading] = useState(false);

    const [works, setWorks] = useState([]);
    const [worksLoading, setWorksLoading] = useState(false);
    const [selectedWorkIds, setSelectedWorkIds] = useState(new Set());
    const [expandedDepartments, setExpandedDepartments] = useState({});
    const [totalCoefficient, setTotalCoefficient] = useState(0);

    // Хранение пользовательских коэффициентов: { workId: customCoefficient }
    const [customCoefficients, setCustomCoefficients] = useState({});
    const [totalCustomCoefficient, setTotalCustomCoefficient] = useState(0);

    // Хранение количества работы на единицу продукта: { workId: quantityCoefficient }
    const [quantityCoefficients, setQuantityCoefficients] = useState({});

    const showSecondSum = true;

    useEffect(() => {
        if (show && product?.id) {
            loadProductPlaces();
            loadProductWorks();
            loadBudgetsAndValues();
            setProductLocal(prev => ({
                ...prev,
                productType: product?.productType ?? 0
            }));
        } else {
            setProductLocal({
                id: product?.id || 0,
                name: product?.name || '',
                coefficient: product?.coefficient || 1,
                categoryId: product?.categoryId || null,
                placeIds: [],
                productType: product?.productType ?? 0
            });
            setWorks([]);
            setSelectedWorkIds(new Set());
            setExpandedDepartments({});
            setTotalCoefficient(0);
            setCustomCoefficients({});
            setTotalCustomCoefficient(0);
            setQuantityCoefficients({});
        }
    }, [show, product?.id]);

    // Пересчёт второй суммы при изменении коэффициента продукта
    useEffect(() => {
        if (!showSecondSum || works.length === 0) {
            if (!showSecondSum) setTotalCustomCoefficient(0);
            return;
        }

        let totalCustom = 0;
        works.forEach(department => {
            department.works.forEach(work => {
                if (selectedWorkIds.has(work.workId)) {
                    const qty = quantityCoefficients[work.workId] ?? 1;
                    const coeff = customCoefficients[work.workId] ?? (work.workCoefficient * productLocal.coefficient);
                    totalCustom += coeff * qty;
                }
            });
        });
        setTotalCustomCoefficient(totalCustom);
    }, [productLocal.coefficient]);

    const loadBudgetsAndValues = async () => {
        setBudgetsLoading(true);
        try {
            // Загружаем список бюджетов
            const budgetsList = await getBudgets(jwt);
            setBudgets(budgetsList || []);

            // Загружаем текущие значения для продукта
            const values = await getProductBudgetValues(product.id, jwt);
            const valuesMap = {};
            (values || []).forEach(v => { valuesMap[v.budgetId] = v.value; });
            setBudgetValues(valuesMap);
        } catch (error) {
            console.error("Ошибка загрузки бюджетов:", error);
        } finally {
            setBudgetsLoading(false);
        }
    };

    const handleBudgetValueChange = (budgetId, value) => {
        setBudgetValues(prev => ({ ...prev, [budgetId]: parseFloat(value) || 0 }));
    };

    const loadProductPlaces = async () => {
        setIsLoadingPlaces(true);
        try {
            const productPlaces = await getNewProductPlaces(product.id, jwt);
            setProductLocal(prev => ({
                ...prev,
                placeIds: productPlaces?.map(p => p.productPlaceId) || []
            }));
        } catch (error) {
            console.error("Ошибка загрузки маркетплейсов:", error);
            alert("Не удалось загрузить привязанные маркетплейсы");
        } finally {
            setIsLoadingPlaces(false);
        }
    };

    const loadProductWorks = async () => {
        setWorksLoading(true);
        try {
            const worksData = await getProductWorks(product.id, jwt);
            setWorks(worksData);

            const selectedIds = new Set();
            const customCoeffs = {};
            const quantityCoeffs = {};
            let totalCoeff = 0;

            worksData.forEach(department => {
                department.works.forEach(work => {
                    if (work.hasProductLink) {
                        const qty = work.quantityCoefficient ?? 1;
                        selectedIds.add(work.workId);
                        totalCoeff += work.workCoefficient * qty;
                        if (work.customCoefficient != null) {
                            customCoeffs[work.workId] = work.customCoefficient;
                        }
                        if (qty !== 1) {
                            quantityCoeffs[work.workId] = qty;
                        }
                    }
                });
            });

            setSelectedWorkIds(selectedIds);
            setCustomCoefficients(customCoeffs);
            setQuantityCoefficients(quantityCoeffs);
            setTotalCoefficient(totalCoeff);

            // Рассчитываем вторую сумму
            let totalCustom = 0;
            worksData.forEach(department => {
                department.works.forEach(work => {
                    if (selectedIds.has(work.workId)) {
                        const qty = quantityCoeffs[work.workId] ?? 1;
                        const coeff = customCoeffs[work.workId] ?? (work.workCoefficient * productLocal.coefficient);
                        totalCustom += coeff * qty;
                    }
                });
            });
            setTotalCustomCoefficient(totalCustom);

            const expanded = {};
            worksData.forEach(dept => {
                expanded[dept.departmentId] = false;
            });
            setExpandedDepartments(expanded);
        } catch (error) {
            console.error("Ошибка загрузки работ:", error);
            alert("Не удалось загрузить работы");
        } finally {
            setWorksLoading(false);
        }
    };

    const toggleDepartment = (departmentId) => {
        setExpandedDepartments(prev => ({
            ...prev,
            [departmentId]: !prev[departmentId]
        }));
    };

    const handleWorkToggle = (workId, workCoefficient, isChecked) => {
        const newSelectedIds = new Set(selectedWorkIds);
        const qty = quantityCoefficients[workId] ?? 1;

        if (isChecked) {
            newSelectedIds.add(workId);
            setTotalCoefficient(prev => prev + workCoefficient * qty);
            const coeff = customCoefficients[workId] ?? (workCoefficient * productLocal.coefficient);
            setTotalCustomCoefficient(prev => prev + coeff * qty);
        } else {
            newSelectedIds.delete(workId);
            setTotalCoefficient(prev => prev - workCoefficient * qty);
            const coeff = customCoefficients[workId] ?? (workCoefficient * productLocal.coefficient);
            setTotalCustomCoefficient(prev => prev - coeff * qty);
        }

        setSelectedWorkIds(newSelectedIds);
    };

    const handleCustomCoefficientChange = (workId, workCoefficient, value) => {
        const newCustomCoeffs = { ...customCoefficients };
        const qty = quantityCoefficients[workId] ?? 1;
        let newTotal = totalCustomCoefficient;

        const oldCoeff = (customCoefficients[workId] ?? (workCoefficient * productLocal.coefficient)) * qty;

        if (value === null || value === undefined || value === '') {
            delete newCustomCoeffs[workId];
            const newCoeff = workCoefficient * productLocal.coefficient * qty;
            newTotal = newTotal - oldCoeff + newCoeff;
        } else {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                newCustomCoeffs[workId] = numValue;
                newTotal = newTotal - oldCoeff + numValue * qty;
            }
        }

        setCustomCoefficients(newCustomCoeffs);
        setTotalCustomCoefficient(newTotal);
    };

    const handleQuantityCoefficientChange = (workId, workCoefficient, value) => {
        const newQty = parseInt(value);
        if (isNaN(newQty) || newQty < 1) return;

        const oldQty = quantityCoefficients[workId] ?? 1;
        const newQuantityCoeffs = { ...quantityCoefficients, [workId]: newQty };

        const baseCoeff = customCoefficients[workId] ?? (workCoefficient * productLocal.coefficient);
        setTotalCoefficient(prev => prev - workCoefficient * oldQty + workCoefficient * newQty);
        setTotalCustomCoefficient(prev => prev - baseCoeff * oldQty + baseCoeff * newQty);
        setQuantityCoefficients(newQuantityCoeffs);
    };

    const togglePlaceSelection = (placeId) => {
        setProductLocal(prev => ({
            ...prev,
            placeIds: prev.placeIds.includes(placeId)
                ? prev.placeIds.filter(id => id !== placeId)
                : [...prev.placeIds, placeId]
        }));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            const productSuccess = await updateNewProduct(productLocal.id, {
                name: productLocal.name,
                coefficient: productLocal.coefficient,
                categoryId: productLocal.categoryId,
                placeIds: productLocal.placeIds,
                productType: productLocal.productType
            }, jwt);

            if (!productSuccess) {
                throw new Error("Не удалось обновить продукт");
            }

            const workIdsArray = Array.from(selectedWorkIds);

            const result = await bulkUpdateProductWorks(productLocal.id, workIdsArray, customCoefficients, quantityCoefficients, jwt);
            if (result === null || result === undefined) {
                throw new Error("Не удалось обновить связи с работами");
            }
            if (typeof result.totalCoefficient === 'number') {
                setTotalCoefficient(result.totalCoefficient);
            }

            const updatedProduct = {
                ...product,
                ...productLocal,
                places: productLocal.placeIds.map(id => ({
                    productPlaceId: id,
                    name: places.find(p => p.id === id)?.name || ''
                }))
            };

            onUpdate(updatedProduct);

            const budgetValuesArray = Object.entries(budgetValues).map(([budgetId, value]) => ({
                budgetId: parseInt(budgetId),
                value: value
            }));
            await updateProductBudgetValues(productLocal.id, budgetValuesArray, jwt);

            onHide();

        } catch (error) {
            console.error("Ошибка при обновлении:", error);
            alert(error.message || "Ошибка сохранения");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteNewProduct(product.id, jwt);
            onDeleted();
            setOnDelete(false);
        } catch (error) {
            console.error("Ошибка при удалении:", error);
            alert("Произошла ошибка при удалении товара");
        }
    };

    if (onDelete) {
        return (
            <Modal centered show={onDelete} onHide={() => setOnDelete(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Подтверждение удаления</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        Вы уверены, что хотите удалить этот товар? Все связанные данные будут также удалены.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setOnDelete(false)}>
                        Отмена
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Удалить
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <Modal centered show={show} onHide={onHide} size="lg" scrollable>
            <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Редактирование товара</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <Form.Group className="mb-3">
                        <Form.Label>Название товара</Form.Label>
                        <Form.Control
                            required
                            type="text"
                            value={productLocal.name}
                            onChange={(e) => setProductLocal({ ...productLocal, name: e.target.value })}
                            placeholder="Введите название товара"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Коэффициент</Form.Label>
                        <Form.Control
                            required
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={productLocal.coefficient}
                            onChange={(e) => setProductLocal({
                                ...productLocal,
                                coefficient: parseFloat(e.target.value) || 0
                            })}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Категория</Form.Label>
                        <Form.Select
                            value={productLocal.categoryId || ''}
                            onChange={(e) => setProductLocal({
                                ...productLocal,
                                categoryId: e.target.value ? parseInt(e.target.value) : null
                            })}
                        >
                            <option value="">Без категории</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Тип продукта</Form.Label>
                        <Form.Select
                            value={productLocal.productType}
                            onChange={(e) => setProductLocal({ ...productLocal, productType: parseInt(e.target.value) })}
                        >
                            <option value={0}>Изделие</option>
                            <option value={1}>Допик</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Доступные маркетплейсы
                            {isLoadingPlaces && (
                                <Spinner animation="border" size="sm" className="ms-2" />
                            )}
                        </Form.Label>
                        <ListGroup>
                            {places.map(place => (
                                <ListGroup.Item
                                    key={place.id}
                                    action
                                    active={productLocal.placeIds.includes(place.id)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        togglePlaceSelection(place.id)
                                    }}
                                    disabled={isLoadingPlaces}
                                >
                                    {place.name}
                                    {productLocal.placeIds.includes(place.id) && (
                                        <span className="float-end">✓</span>
                                    )}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Form.Group>

                    <hr />

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Привязка к работам
                            {worksLoading && (
                                <Spinner animation="border" size="sm" className="ms-2" />
                            )}
                        </Form.Label>

                        <Alert variant="info" className="mb-3">
                            <strong>Сумма привязанных работ:</strong> {totalCoefficient.toFixed(2)}
                        </Alert>

                        {showSecondSum && (
                            <Alert variant="warning" className="mb-3">
                                <strong>Сумма с учётом коэф. продукта ({productLocal.coefficient}):</strong> {totalCustomCoefficient.toFixed(2)}
                            </Alert>
                        )}

                        {worksLoading ? (
                            <div className="text-center">
                                <Spinner animation="border" />
                                <p className="mt-2">Загрузка работ...</p>
                            </div>
                        ) : works.length === 0 ? (
                            <Alert variant="info">
                                Нет доступных работ. Сначала создайте работы в разделе "Направления и работы производства".
                            </Alert>
                        ) : (
                            <div className="works-container">
                                {works.map(department => (
                                    <div key={department.departmentId} className="mb-3">
                                        <div
                                            className="department-header p-2 bg-light rounded d-flex justify-content-between align-items-center"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => toggleDepartment(department.departmentId)}
                                        >
                                            <div>
                                                <strong>{department.departmentName}</strong>
                                                <Badge bg="secondary" className="ms-2">
                                                    {department.works.filter(w => selectedWorkIds.has(w.workId)).length}/{department.works.length}
                                                </Badge>
                                                {expandedDepartments[department.departmentId] ? (
                                                    <span className="ms-2">▲</span>
                                                ) : (
                                                    <span className="ms-2">▼</span>
                                                )}
                                            </div>
                                        </div>

                                        <Collapse in={expandedDepartments[department.departmentId]}>
                                            <div>
                                                <Table bordered size="sm" className="mt-2">
                                                    <thead>
                                                        <tr>
                                                            <th>Работа</th>
                                                            <th style={{ width: '100px' }}>Коэффициент</th>
                                                            <th style={{ width: '80px' }}>Кол-во</th>
                                                            {showSecondSum && (
                                                                <th style={{ width: '140px' }}>Коэф. с учётом продукта</th>
                                                            )}
                                                            <th style={{ width: '80px' }} className="text-center">Привязать</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {department.works.map(work => (
                                                            <tr key={work.workId}>
                                                                <td>
                                                                    {work.workName}
                                                                </td>
                                                                <td>
                                                                    <Badge bg="info">
                                                                        {work.workCoefficient}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        step="1"
                                                                        size="sm"
                                                                        value={quantityCoefficients[work.workId] ?? 1}
                                                                        onChange={(e) => handleQuantityCoefficientChange(
                                                                            work.workId,
                                                                            work.workCoefficient,
                                                                            e.target.value
                                                                        )}
                                                                        disabled={!selectedWorkIds.has(work.workId)}
                                                                        style={{ width: '60px' }}
                                                                    />
                                                                </td>
                                                                {showSecondSum && (
                                                                    <td>
                                                                        <Form.Control
                                                                            type="number"
                                                                            step="0.01"
                                                                            size="sm"
                                                                            value={
                                                                                customCoefficients[work.workId] ??
                                                                                (selectedWorkIds.has(work.workId)
                                                                                    ? (work.workCoefficient * productLocal.coefficient).toFixed(2)
                                                                                    : '')
                                                                            }
                                                                            onChange={(e) => handleCustomCoefficientChange(
                                                                                work.workId,
                                                                                work.workCoefficient,
                                                                                e.target.value === '' ? '' : e.target.value
                                                                            )}
                                                                            disabled={!selectedWorkIds.has(work.workId)}
                                                                            style={{ width: '120px' }}
                                                                        />
                                                                    </td>
                                                                )}
                                                                <td className="text-center">
                                                                    <Form.Check
                                                                        type="checkbox"
                                                                        checked={selectedWorkIds.has(work.workId)}
                                                                        onChange={(e) => handleWorkToggle(work.workId, work.workCoefficient, e.target.checked)}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Collapse>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Form.Group>
                    <hr />
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Бюджеты
                            {budgetsLoading && <Spinner animation="border" size="sm" className="ms-2" />}
                        </Form.Label>
                        {budgetsLoading ? (
                            <div className="text-center"><Spinner animation="border" /></div>
                        ) : budgets.length === 0 ? (
                            <Alert variant="info">Нет созданных бюджетов</Alert>
                        ) : (
                            <Table bordered size="sm">
                                <thead>
                                    <tr>
                                        <th>Бюджет</th>
                                        <th style={{ width: '150px' }}>Значение</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {budgets.map(budget => (
                                        <tr key={budget.id}>
                                            <td>{budget.name}</td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    value={budgetValues[budget.id] ?? 0}
                                                    onChange={(e) => handleBudgetValueChange(budget.id, e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="danger"
                        onClick={() => setOnDelete(true)}
                        disabled={isSubmitting}
                    >
                        Удалить
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={onHide}
                        disabled={isSubmitting}
                    >
                        Отмена
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Сохранение...
                            </>
                        ) : 'Сохранить'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}